import React, { useEffect } from "react";
import clsx from "clsx";
import { Handle, Position, useUpdateNodeInternals } from "react-flow-renderer";
import { makeStyles } from "@material-ui/core/styles";
import WarnIcon from "@material-ui/icons/ReportProblemOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import LoopIcon from "@material-ui/icons/Loop";
import IterIcon from "@material-ui/icons/RedoOutlined";
import Tooltip from "@material-ui/core/Tooltip";
import LoopEndIcon from "@material-ui/icons/ArrowDownwardRounded";
import PlayIcon from "@material-ui/icons/PlayArrowRounded";
import HelpIcon from "@material-ui/icons/HelpOutline";

import { VERBS, shortVerbTitle, connectorTypeOf } from "./verbs";
import { TRIGGERS, triggerSummary } from "./triggers";
import { useBuilderActions } from "./builderActions";
import NodeIcon from "./NodeIcon";

// Nós com handles DINÂMICOS (condicional/URA/menu: um handle por caso/dígito/opção)
// precisam avisar o react-flow quando o conjunto de handles muda — senão as
// ARESTAS ligadas a esses handles não desenham (parecem "desligadas") e os handles
// ficam difíceis de conectar. Chama updateNodeInternals na montagem e a cada
// mudança do conjunto de chaves.
function useDynamicHandles(nodeId, keys) {
  const updateNodeInternals = useUpdateNodeInternals();
  const sig = (keys || []).join("|");
  useEffect(() => {
    if (nodeId) updateNodeInternals(nodeId);
  }, [nodeId, sig, updateNodeInternals]);
}

// Sistema de nós (refactor): CARD NEUTRO; a cor vive só no chip de ícone (e num
// filete de 3px nos tipos especiais). A cor INFORMA a categoria, não polui.
// Cores de acento por categoria/verbo (alinhadas a theme.app):
const ACC = {
  green: "#1E9E62", // voz / chamada (originate, say)
  teal: "#2A8C8C", // coleta (gather, ivr)
  blue: "#2F6FB0", // integração / HTTP / dados
  orange: "#C77A12", // IA
  violet: "#6A52C4", // gatilho + lógica/fluxo
  red: "#C0453B", // fim / terminal
  char: "#2C353D",
};
const LOOP = ACC.violet; // família do bloco de repetição (flow-control)
const VERB_ACCENT = {
  hangup: ACC.red,
  "sip:decline": ACC.red,
  "sip:refer": ACC.red,
  leave: ACC.red,
  loop: ACC.violet,
  loop_end: ACC.violet,
};
const CAT_ACCENT = {
  audio: ACC.green,
  call: ACC.green,
  input: ACC.teal,
  queue: ACC.teal,
  routing: ACC.violet,
  advanced: ACC.blue,
  data: ACC.blue,
  connectors: ACC.blue,
  ai: ACC.orange,
};
const nodeAccent = (verbKey, category) => VERB_ACCENT[verbKey] || CAT_ACCENT[category] || ACC.char;
// reusado pelo Picker de Variáveis (cabeçalho de grupo = nó), p/ a cor nunca divergir do canvas
export { nodeAccent };

const isEmpty = v => v == null || v === "" || (Array.isArray(v) && v.length === 0);
// primeiro campo OBRIGATÓRIO vazio (estado "incompleto" — mostra na cara do nó)
const missingField = (def, data) => {
  if (!def || !def.fields) return null;
  for (const f of def.fields) if (f.required && isEmpty((data || {})[f.name])) return f.label;
  return null;
};
// resumo com variáveis {{...}} em mono
const renderSummary = (text, cls) => {
  if (text == null || text === "") return null;
  return String(text)
    .split(/(\{\{[^}]+\}\})/g)
    .map((p, i) =>
      /^\{\{/.test(p) ? (
        <span key={i} className={cls}>{p}</span>
      ) : (
        <React.Fragment key={i}>{p}</React.Fragment>
      )
    );
};

const useStyles = makeStyles(theme => {
  const a = theme.app;
  return {
    card: {
      width: 290,
      background: "#fff",
      border: `1px solid ${a.border}`,
      borderRadius: 12,
      boxShadow: "0 3px 12px rgba(20,25,32,.06)",
      position: "relative",
      transition: "box-shadow .15s",
      "&:hover": { boxShadow: "0 6px 16px rgba(20,25,32,.10)" },
    },
    nhead: { display: "flex", alignItems: "center", gap: 10, padding: "11px 13px" },
    ntitle: { fontSize: 13.5, fontWeight: 600, lineHeight: 1.15, color: a.ink, display: "flex", alignItems: "center", gap: 6, minWidth: 0 },
    ntag: { marginLeft: "auto", fontFamily: a.mono, fontSize: 10.5, color: a.textTertiary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 96, flexShrink: 0, paddingLeft: 6 },
    nbody: { padding: "0 13px 12px 53px", marginTop: -3, fontSize: 12, color: a.textSecondary, lineHeight: 1.4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    val: { fontFamily: a.mono, fontSize: 11.5, color: a.ink },
    port: { width: 10, height: 10, borderRadius: "50%", background: "#fff", border: "2px solid #B9C0C7", zIndex: 5 },
    sel: {
      boxShadow: `0 0 0 2px ${a.dark}, 0 6px 18px rgba(20,25,32,.12)`,
      // mantém o anel de seleção também no hover (senão o &:hover do card,
      // mais específico, sobrescreve o box-shadow e a borda de seleção some)
      "&:hover": { boxShadow: `0 0 0 2px ${a.dark}, 0 6px 18px rgba(20,25,32,.16)` },
    },
    // EXECUTANDO AGORA: um brilho dá a volta na borda, no sentido horário — lê-se
    // como "em andamento", que é o que o nó está fazendo. Nada de piscar: pulso
    // pede atenção, volta indica processo.
    //
    // Como: um ::before 2px maior que o card recebe um conic-gradient e uma
    // MÁSCARA que vaza só o miolo (content-box XOR caixa inteira), sobrando o
    // anel. O ângulo do gradiente é que gira — o elemento fica parado, senão os
    // cantos arredondados dançariam junto. O @property e o keyframe do ângulo
    // moram no index.css (o JSS do MUI v4 não emite @property).
    //
    // Azul de propósito: o lime já quer dizer "recém-criado pelo copiloto"
    // (hlCard) e o escuro quer dizer "selecionado". Três significados, três cores.
    rodando: {
      // o ::before se posiciona por este elemento (o card já é relative; o
      // cabeçalho/rodapé do bloco de repetição, não)
      position: "relative",
      boxShadow: `0 0 0 1px ${ACC.blue}40, 0 4px 16px rgba(47,111,176,.18)`,
      "&::before": {
        content: '""',
        position: "absolute",
        top: -2,
        right: -2,
        bottom: -2,
        left: -2,
        borderRadius: 14,
        padding: 2,
        pointerEvents: "none",
        background: `conic-gradient(from var(--nb-angulo), rgba(47,111,176,0) 0deg, rgba(47,111,176,0) 210deg, ${ACC.blue} 300deg, #9CC8F2 340deg, rgba(47,111,176,0) 360deg)`,
        WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
        WebkitMaskComposite: "xor",
        mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
        maskComposite: "exclude",
        animation: "nbGiraBrilho 1.6s linear infinite",
      },
      // movimento reduzido: o brilho para e fica um anel contínuo
      "@media (prefers-reduced-motion: reduce)": {
        "&::before": { animation: "none", background: ACC.blue },
      },
    },
    warnCard: { borderColor: "#E7C48B" },
    errCard: { borderColor: a.danger },
    pill: { display: "inline-flex", alignItems: "center", gap: 5, marginLeft: "auto", fontSize: 10.5, fontWeight: 600, padding: "2px 7px", borderRadius: 6, flexShrink: 0, whiteSpace: "nowrap", "& svg": { fontSize: 12 } },
    pillWarn: { color: "#9A6410", background: "#FBF1DE" },
    // realce transitório do nó recém-criado pelo copiloto (anel + glow verde).
    // Verde só como acento sobre o canvas claro, com glow (handoff §6).
    hlCard: { boxShadow: `0 0 0 2px ${a.lime}, 0 0 26px rgba(206,255,0,.42)` },
    // entrada do nó recém-criado: fade + micro-scale ~180ms (handoff §1). Desligada
    // quando o usuário pede movimento reduzido (prefers-reduced-motion).
    "@keyframes nbNodeEnter": { from: { opacity: 0, transform: "scale(.92)" }, to: { opacity: 1, transform: "scale(1)" } },
    enterCard: { "@media (prefers-reduced-motion: no-preference)": { animation: "$nbNodeEnter 180ms cubic-bezier(.2,.7,.3,1.2) both" } },
    createdBadge: { position: "absolute", top: -8, right: -6, background: a.lime, color: "#10140f", fontSize: 10, fontWeight: 700, lineHeight: 1, letterSpacing: ".02em", padding: "3px 7px", borderRadius: 10, boxShadow: "0 2px 6px rgba(20,25,32,.18)", pointerEvents: "none", zIndex: 6 },
    // pendência de template de WhatsApp no nó (clicável → abre o modal)
    tplBadge: { display: "inline-flex", alignItems: "center", gap: 5, margin: "0 13px 11px 53px", fontSize: 10.5, fontWeight: 700, letterSpacing: ".02em", padding: "3px 9px", borderRadius: 7, cursor: "pointer", color: "#fff", "& svg": { fontSize: 13 }, "&:hover": { filter: "brightness(1.06)" } },
    offTag: { fontSize: 9.5, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#fff", background: "rgba(0,0,0,0.35)", borderRadius: 6, padding: "0 5px" },
    runBtn: { display: "inline-flex", alignItems: "center", gap: 4, margin: "0 13px 12px 53px", fontSize: 12, fontWeight: 700, padding: "5px 12px", borderRadius: 8, cursor: "pointer", color: "#fff", border: "none", background: ACC.violet, fontFamily: "inherit", "&:hover": { filter: "brightness(1.07)" }, "&:disabled": { opacity: 0.6, cursor: "default" } },
    disabledCard: { borderStyle: "dashed" },
    dimmed: { opacity: 0.5, filter: "grayscale(0.9)" },
    viewCard: { cursor: "default" },
    // ramificação / múltiplas saídas rotuladas
    outs: { display: "flex", padding: "8px 0 11px", borderTop: `1px solid ${a.lineSoft}` },
    outb: { flex: 1, textAlign: "center", fontFamily: a.mono, fontSize: 10.5, color: a.textSecondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", padding: "0 4px" },
    // gatilho
    helpBtn: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 22,
      height: 22,
      borderRadius: 6,
      flexShrink: 0,
      color: a.textTertiary,
      cursor: "pointer",
      "&:hover": { background: a.hover, color: a.ink },
    },
    triggerTag: { marginLeft: "auto", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: a.textTertiary, flexShrink: 0 },

    // ── canvas vazio: "Como quer começar?" (IA ou montar à mão) ──
    startCard: {
      width: 340,
      background: "#fff",
      border: `1px solid ${a.border}`,
      borderRadius: 16,
      boxShadow: "0 10px 30px rgba(27,34,42,.10)",
      padding: 14,
      display: "flex",
      flexDirection: "column",
      gap: 8,
    },
    startTitle: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.07em",
      textTransform: "uppercase",
      color: a.textTertiary,
      textAlign: "center",
      padding: "2px 0 6px",
    },
    startOpt: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "11px 13px",
      borderRadius: 12,
      border: `1px solid ${a.border}`,
      background: "#fff",
      cursor: "pointer",
      transition: "background .12s, border-color .12s",
      "&:hover": { background: a.hover, borderColor: a.borderInput },
    },
    startIcon: {
      width: 34,
      height: 34,
      flexShrink: 0,
      borderRadius: 10,
      background: a.chip,
      color: a.textSecondary,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 18,
      fontWeight: 700,
      lineHeight: 1,
    },
    // acento de IA: verde só sobre o charcoal, nunca sobre branco (guia do app)
    startIconAi: { background: a.dark, color: a.lime },
    startTexts: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
    startOptTitle: { fontSize: 13.5, fontWeight: 600, color: a.ink, lineHeight: 1.2 },
    startOptDesc: { fontSize: 12, color: a.textSecondary, lineHeight: 1.3 },
    // "+" leg (adicionar próximo passo)
    leg: { position: "absolute", top: "100%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center" },
    legLine: { width: 0, height: 20, borderLeft: "2px solid #CDD3D9" },
    legBtn: { width: 20, height: 20, borderRadius: "50%", border: `1px solid ${a.borderInput}`, background: "#fff", color: a.textSecondary, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, lineHeight: 1, cursor: "pointer", boxShadow: "0 1px 2px rgba(44,53,61,0.10)", "&:hover": { borderColor: a.lime, color: a.dark, background: a.selectedRow } },

    // ── Bloco de repetição (container, Modelo B: overlay sobre os marcadores) ──
    // frame tintado desenhado ATRÁS dos passos (nó "loopFrame", não-interativo)
    frame: { width: "100%", height: "100%", background: "#ECE6FB", border: "1px solid rgba(106,82,196,.32)", borderRadius: 16, boxShadow: "0 6px 20px rgba(106,82,196,.12)", position: "relative", pointerEvents: "none" },
    spine: { position: "absolute", left: 18, width: 2, borderRadius: 2, background: "rgba(106,82,196,.40)" },
    spineDot: { position: "absolute", left: -3, width: 8, height: 8, borderRadius: "50%", background: LOOP },
    // wrapper do cabeçalho (barra violeta + subtítulo + corpo do "adicionar")
    loopHeadWrap: { width: 344, position: "relative" },
    // cabeçalho: SÓ o título "Repetir para cada item" (barra violeta, topo flush)
    loopHead: { width: 344, background: LOOP, color: "#fff", borderRadius: "16px 16px 0 0", overflow: "hidden" },
    // subtítulo: a expressão de iteração, ABAIXO do header, no tom claro, separada
    // do conteúdo por uma régua (border-bottom).
    loopSub: { width: 344, padding: "9px 16px", fontSize: 12, color: a.textSecondary, borderBottom: "1px solid rgba(106,82,196,.20)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    loopSubV: { fontFamily: a.mono, fontSize: 11.5, color: LOOP },
    // corpo do bloco vazio: só o botão "adicionar passo dentro" (o frame tintado
    // atrás dá o fundo); ensina que os passos vão PRA DENTRO.
    loopEmpty: { padding: "10px 14px 12px 34px" },
    addInside: { width: "100%", border: "1.5px dashed rgba(106,82,196,.4)", background: "rgba(106,82,196,.05)", color: LOOP, borderRadius: 10, padding: "9px 10px", fontSize: 12.5, fontWeight: 400, fontFamily: "inherit", cursor: "pointer", "&:hover": { background: "rgba(106,82,196,.1)" } },
    loopHeadRow: { display: "flex", alignItems: "center", gap: 9, padding: "12px 16px" },
    loopHeadTitle: { fontSize: 14, fontWeight: 600, lineHeight: 1.1 },
    loopCount: { marginLeft: "auto", fontFamily: a.mono, fontSize: 11, background: "rgba(255,255,255,.18)", padding: "3px 8px", borderRadius: 6, whiteSpace: "nowrap" },
    // rodapé do container (era o nó "fim") — barra de largura cheia, flush na base
    loopFoot: { width: 344, background: "#DED5F5", borderRadius: "0 0 16px 16px", display: "flex", alignItems: "center", gap: 8, padding: "11px 16px", color: LOOP, fontWeight: 600, fontSize: 12.5, "& svg": { fontSize: 17 } },
    loopFootSmall: { marginLeft: "auto", fontWeight: 400, color: a.textTertiary, fontSize: 11 },
  };
});

// posição horizontal (%) da i-ésima de n portas de base (centro de cada célula)
const handleLeft = (i, n) => `${((i + 0.5) / n) * 100}%`;

// "+" leg sob cada porta de saída para adicionar o próximo nó
function AddLegs({ nodeId, handles }) {
  const classes = useStyles();
  const { onAddAfter, readOnly } = useBuilderActions();
  if (readOnly) return null;
  const n = handles.length;
  return (
    <>
      {handles.map((h, i) => (
        <div key={h.id} className={classes.leg} style={{ left: handleLeft(i, n) }}>
          <div className={classes.legLine} />
          <div
            className={`${classes.legBtn} nodrag`}
            title="Adicionar passo"
            onMouseDown={e => e.stopPropagation()}
            onClick={e => {
              e.stopPropagation();
              onAddAfter(nodeId, h.id);
            }}
          >
            +
          </div>
        </div>
      ))}
    </>
  );
}

// rótulos + portas de base para nós com 2+ saídas (condição/AMD/URA)
function MultiOuts({ classes, handles }) {
  return (
    <>
      <div className={classes.outs}>
        {handles.map(h => (
          <span key={h.id} className={classes.outb}>{h.label || h.id}</span>
        ))}
      </div>
      {handles.map((h, i) => (
        <Handle key={h.id} type="source" id={h.id} position={Position.Bottom} className={classes.port} style={{ left: handleLeft(i, handles.length) }} />
      ))}
    </>
  );
}

// sparkle (✦) — mesmo acento de IA do copiloto. Redefinido aqui (e não importado
// do CopilotPanel) para não acoplar o canvas ao painel por causa de um ícone.
const Sparkle = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden focusable="false">
    <path d="M12 1c.4 5.8 4.2 9.6 11 10-6.8.4-10.6 4.2-11 11-.4-6.8-4.2-10.6-11-11 6.8-.4 10.6-4.2 11-10z" />
  </svg>
);

export function TriggerNode({ id, data, selected }) {
  const classes = useStyles();
  const { readOnly, onRunManual, onPickTrigger, onOpenCopilot, onOpenSetup, onIterateList, nodeRodando, heroActive } = useBuilderActions();
  const rodando = !!id && nodeRodando === id; // executando agora
  const trig = data.triggerType ? TRIGGERS[data.triggerType] : null;
  const [running, setRunning] = React.useState(false);

  if (!trig) {
    // HERO ativo (nova automação): o hero no painel do chat é a porta de entrada,
    // então o canvas 30% fica só com a grade — sem o card "Como quer começar?".
    if (heroActive) return <Handle type="source" id="next" position={Position.Bottom} className={classes.port} style={{ opacity: 0 }} />;
    // canvas vazio: as DUAS formas de começar. Antes era só "Adicionar gatilho",
    // e quem não sabia montar à mão não descobria que dava para descrever e
    // deixar a IA montar. O clique fica nas opções (não no card), por isso o
    // stopPropagation — o canvas abria o modal de gatilho ao clicar em qualquer
    // ponto do nó.
    const pick = (e, fn) => {
      e.stopPropagation();
      fn();
    };
    return (
      <div className={classes.startCard}>
        <div className={classes.startTitle}>Como quer começar?</div>
        {onOpenCopilot && (
          <div className={clsx(classes.startOpt, "nodrag")} onClick={e => pick(e, onOpenCopilot)}>
            <span className={clsx(classes.startIcon, classes.startIconAi)}>
              <Sparkle size={17} />
            </span>
            <span className={classes.startTexts}>
              <span className={classes.startOptTitle}>Criar com IA</span>
              <span className={classes.startOptDesc}>Descreva e a Numera monta o fluxo</span>
            </span>
          </div>
        )}
        <div className={clsx(classes.startOpt, "nodrag")} onClick={e => pick(e, onPickTrigger)}>
          <span className={classes.startIcon}>+</span>
          <span className={classes.startTexts}>
            <span className={classes.startOptTitle}>Adicionar gatilho</span>
            <span className={classes.startOptDesc}>Montar manualmente, nó por nó</span>
          </span>
        </div>
        <Handle type="source" id="next" position={Position.Bottom} className={classes.port} />
      </div>
    );
  }

  const accent = ACC.violet; // gatilho = violeta (handoff §3); sem porta de entrada
  return (
    <div
      className={`${classes.card} ${readOnly ? classes.viewCard : ""} ${selected ? classes.sel : ""} ${rodando ? classes.rodando : ""}`}
      style={{ borderTop: `3px solid ${accent}` }}
    >
      <div className={classes.nhead}>
        <NodeIcon triggerType={data.triggerType} color={accent} size={30} chip />
        <span className={classes.ntitle}>{data.label || trig.title}</span>
        {/* gatilho que exige configuração DO OUTRO LADO (ex.: ação de campanha no
            Mautic): sem o guia o cliente ativa e fica esperando evento que nunca vem */}
        {trig.setup && onOpenSetup && (
          <span
            className={clsx(classes.helpBtn, "nodrag")}
            title="Como configurar na ferramenta"
            onClick={e => {
              e.stopPropagation();
              onOpenSetup(trig.setup);
            }}
          >
            <HelpIcon style={{ fontSize: 16 }} />
          </span>
        )}
        {data._listToken && !readOnly && (
          <Tooltip title="Repetir para cada item desta lista" placement="top" arrow>
            <span
              className="nodrag"
              onMouseDown={e => e.stopPropagation()}
              onClick={e => { e.stopPropagation(); if (onIterateList) onIterateList(id, data._listToken); }}
              style={{ display: "inline-flex", cursor: "pointer", color: LOOP, flexShrink: 0 }}
            >
              <IterIcon style={{ fontSize: 19 }} />
            </span>
          </Tooltip>
        )}
        <span className={classes.ntag}>gatilho</span>
      </div>
      <div className={classes.nbody}>{triggerSummary(data.triggerType, data) || "—"}</div>
      {trig.manualRun && !readOnly && onRunManual && (
        <button
          type="button"
          className={classes.runBtn}
          disabled={running}
          title="Rodar o fluxo publicado agora"
          onClick={async e => {
            e.stopPropagation();
            if (running) return;
            setRunning(true);
            try {
              await onRunManual();
            } finally {
              setRunning(false);
            }
          }}
        >
          <PlayIcon style={{ fontSize: 18 }} /> {running ? "Executando…" : "Executar"}
        </button>
      )}
      <Handle type="source" id="next" position={Position.Bottom} className={classes.port} />
      <AddLegs nodeId={id} handles={[{ id: "next" }]} />
    </div>
  );
}

// pendência de template de WhatsApp no nó (estados não-aprovados)
const TPL_STATUS = {
  DRAFT: { label: "Template a criar", color: "#5B6470" },
  PENDING: { label: "Template aguardando aprovação", color: "#B26A00" },
  REJECTED: { label: "Template rejeitado", color: "#B12A2A" },
  FAILED: { label: "Template falhou", color: "#B12A2A" },
};

export function VerbNode({ id, data, selected }) {
  const classes = useStyles();
  const { readOnly, onAddAfter, onOpenTemplates, onIterateList, nodeRodando, agentNames } = useBuilderActions();
  const rodando = !!id && nodeRodando === id; // executando agora
  const def = VERBS[data.verbKey];
  if (!def) return null;

  // Bloco de repetição: o "início" vira CABEÇALHO e o "fim" vira RODAPÉ do
  // container (o frame tintado é desenhado atrás, por LoopFrameNode).
  if (data.verbKey === "loop") {
    const noList = !readOnly && isEmpty(data.list);
    const empty = !!data._empty;
    return (
      <div className={`${classes.loopHeadWrap} ${selected ? classes.sel : ""} ${rodando ? classes.rodando : ""}`}>
        <Handle type="target" position={Position.Top} className={classes.port} />
        <div className={classes.loopHead}>
          <div className={classes.loopHeadRow}>
            <LoopIcon style={{ fontSize: 18 }} />
            <span className={classes.loopHeadTitle}>Repetir para cada item</span>
            <span className={classes.loopCount}>por item</span>
          </div>
        </div>
        <div className={classes.loopSub}>
          {noList ? (
            "defina a lista a percorrer"
          ) : (
            <>
              para cada {data.itemAs || "item"} em{" "}
              <span className={classes.loopSubV}>{data.list || "…"}</span>
            </>
          )}
        </div>
        {empty && !readOnly && (
          <div className={classes.loopEmpty}>
            <button
              type="button"
              className={`${classes.addInside} nodrag`}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => {
                e.stopPropagation();
                onAddAfter(id, "next");
              }}
            >
              Adicionar ação
            </button>
          </div>
        )}
        <Handle type="source" id="next" position={Position.Bottom} className={classes.port} />
        {!empty && <AddLegs nodeId={id} handles={[{ id: "next" }]} />}
      </div>
    );
  }
  if (data.verbKey === "loop_end") {
    return (
      <div className={`${classes.loopFoot} ${selected ? classes.sel : ""} ${rodando ? classes.rodando : ""}`}>
        <Handle type="target" position={Position.Top} className={classes.port} />
        <LoopEndIcon />
        Fim da repetição
        <span className={classes.loopFootSmall}>repete por item</span>
        <Handle type="source" id="next" position={Position.Bottom} className={classes.port} />
        <AddLegs nodeId={id} handles={[{ id: "next" }]} />
      </div>
    );
  }

  const handles = def.handles || [];
  const terminal = !!def.terminal;
  const accent = nodeAccent(data.verbKey, def.category);
  const missing = !readOnly ? missingField(def, data) : null;
  // nós de agente de IA: o card mostra QUAL agente está configurado
  const agentName = data.agentId && agentNames ? agentNames[String(data.agentId)] : undefined;
  const agentLine = data.agentId ? agentName || (agentName === null ? `Agente #${data.agentId} (não encontrado)` : `Agente #${data.agentId}`) : "";
  // nó "Rodar script": mostra QUAL script (+ "modo de teste")
  const jobName = data.jobId && agentNames ? agentNames[String(data.jobId)] : undefined;
  const jobLine = data.jobId ? `${jobName || (jobName === null ? `Script #${data.jobId} (não encontrado)` : `Script #${data.jobId}`)}${data.dryRun ? " · teste" : ""}` : "";
  const ownSummary = def.summary ? def.summary(data) : "";
  const summary = jobLine || (agentLine && !ownSummary ? agentLine : ownSummary);
  const multi = handles.length > 1;
  // conectores: título CURTO (sem o prefixo da marca — o chip + a tag dão a marca)
  // e tag enxuta "conn.<marca>"; o resto do contexto vem do corpo (valor-chave).
  const conn = connectorTypeOf(data.verbKey);
  const title = data.label || (conn ? shortVerbTitle(def.title) : def.title);

  return (
    <div
      className={`${classes.card} ${missing ? classes.warnCard : ""} ${readOnly ? classes.viewCard : ""} ${data.disabled ? classes.disabledCard : ""} ${data.disabled && !selected ? classes.dimmed : ""} ${selected ? classes.sel : ""} ${rodando ? classes.rodando : ""} ${data._hl ? classes.hlCard + " " + classes.enterCard : ""}`}
      style={terminal ? { borderBottom: `3px solid ${ACC.red}` } : undefined}
    >
      {data._hl && <div className={classes.createdBadge}>+ criado</div>}
      <Handle type="target" position={Position.Top} className={classes.port} />
      <div className={classes.nhead}>
        <NodeIcon verbKey={data.verbKey} category={def.category} color={accent} size={30} chip />
        <span className={classes.ntitle}>
          {title}
          {data.disabled && <span className={classes.offTag}>off</span>}
        </span>
        {missing && (
          <span className={`${classes.pill} ${classes.pillWarn}`}>
            <WarnIcon /> configurar
          </span>
        )}
        {data._listToken && !readOnly && (
          <Tooltip title="Repetir para cada item desta lista" placement="top" arrow>
            <span
              className="nodrag"
              onMouseDown={e => e.stopPropagation()}
              onClick={e => { e.stopPropagation(); if (onIterateList) onIterateList(id, data._listToken); }}
              style={{ marginLeft: missing ? 6 : "auto", display: "inline-flex", cursor: "pointer", color: LOOP, flexShrink: 0 }}
            >
              <IterIcon style={{ fontSize: 19 }} />
            </span>
          </Tooltip>
        )}
      </div>
      <div className={classes.nbody}>
        {missing ? `Configure "${missing}"` : renderSummary(summary, classes.val) || "—"}
      </div>

      {data.templateDraft && data.templateDraft.status !== "APPROVED" && (() => {
        const ts = TPL_STATUS[data.templateDraft.status] || TPL_STATUS.DRAFT;
        return (
          <div
            className={classes.tplBadge}
            style={{ background: ts.color }}
            title="Abrir templates de WhatsApp"
            onClick={e => { e.stopPropagation(); if (onOpenTemplates) onOpenTemplates(id); }}
          >
            <WhatsAppIcon /> {ts.label}
          </div>
        );
      })()}

      {multi ? (
        <MultiOuts classes={classes} handles={handles} />
      ) : (
        handles.map((h, i) => (
          <Handle key={h.id} type="source" id={h.id} position={Position.Bottom} className={classes.port} style={{ left: handleLeft(i, handles.length) }} />
        ))
      )}
      <AddLegs nodeId={id} handles={handles} />
    </div>
  );
}

export function BranchNode({ id, data, selected }) {
  const classes = useStyles();
  const { readOnly, nodeRodando } = useBuilderActions();
  const rodando = !!id && nodeRodando === id; // executando agora
  const def = VERBS.branch;
  const keys = [...(data.caseKeys || []), "default", "timeout"];
  useDynamicHandles(id, keys);
  const handles = keys.map(k => ({ id: k, label: k }));
  const accent = nodeAccent("branch", def.category);

  return (
    <div
      className={`${classes.card} ${readOnly ? classes.viewCard : ""} ${data.disabled ? classes.disabledCard : ""} ${selected ? classes.sel : ""} ${rodando ? classes.rodando : ""}`}
      style={{ minWidth: 290, width: Math.max(290, 80 + keys.length * 48) }}
    >
      <Handle type="target" position={Position.Top} className={classes.port} />
      <div className={classes.nhead}>
        <NodeIcon verbKey="branch" category={def.category} color={accent} size={30} chip />
        <span className={classes.ntitle}>
          {data.label || def.title}
          {data.disabled && <span className={classes.offTag}>off</span>}
        </span>
        <span className={classes.ntag}>branch</span>
      </div>
      <div className={classes.nbody}>
        campo: <span className={classes.val}>{data.field || "digits"}</span>
      </div>
      <MultiOuts classes={classes} handles={handles} />
      <AddLegs nodeId={id} handles={handles} />
    </div>
  );
}

export function IvrNode({ id, data, selected }) {
  const classes = useStyles();
  const { readOnly, nodeRodando } = useBuilderActions();
  const rodando = !!id && nodeRodando === id; // executando agora
  const def = VERBS.ivr;
  const keys = [...(data.digits || []), "noinput"];
  useDynamicHandles(id, keys);
  const label = k => (k === "noinput" ? "s/ resp." : k === "star" ? "*" : k === "pound" ? "#" : k);
  const handles = keys.map(k => ({ id: k, label: label(k) }));
  const accent = nodeAccent("ivr", def.category);

  return (
    <div
      className={`${classes.card} ${readOnly ? classes.viewCard : ""} ${data.disabled ? classes.disabledCard : ""} ${selected ? classes.sel : ""} ${rodando ? classes.rodando : ""}`}
      style={{ minWidth: 290, width: Math.max(290, 90 + keys.length * 44) }}
    >
      <Handle type="target" position={Position.Top} className={classes.port} />
      <div className={classes.nhead}>
        <NodeIcon verbKey="ivr" category={def.category} color={accent} size={30} chip />
        <span className={classes.ntitle}>
          {data.label || def.title}
          {data.disabled && <span className={classes.offTag}>off</span>}
        </span>
        <span className={classes.ntag}>ivr</span>
      </div>
      <div className={classes.nbody}>{(data.prompt || "").slice(0, 42) || "menu"}</div>
      <MultiOuts classes={classes} handles={handles} />
      <AddLegs nodeId={id} handles={handles} />
    </div>
  );
}

export function WaMenuNode({ id, data, selected }) {
  const classes = useStyles();
  const { readOnly, nodeRodando } = useBuilderActions();
  const rodando = !!id && nodeRodando === id; // executando agora
  const def = VERBS.wa_menu;
  const opts = (data.options || []).filter(Boolean);
  const keys = [...opts, "default"];
  useDynamicHandles(id, keys);
  const lbl = k => (k === "default" ? "s/ opção" : k);
  const handles = keys.map(k => ({ id: k, label: lbl(k) }));
  const accent = nodeAccent("wa_menu", def.category);

  return (
    <div
      className={`${classes.card} ${readOnly ? classes.viewCard : ""} ${data.disabled ? classes.disabledCard : ""} ${selected ? classes.sel : ""} ${rodando ? classes.rodando : ""}`}
      style={{ minWidth: 300, width: Math.max(300, 90 + keys.length * 88) }}
    >
      <Handle type="target" position={Position.Top} className={classes.port} />
      <div className={classes.nhead}>
        <NodeIcon verbKey="wa_menu" category={def.category} color={accent} size={30} chip />
        <span className={classes.ntitle}>
          {data.label || def.title}
          {data.disabled && <span className={classes.offTag}>off</span>}
        </span>
        <span className={classes.ntag}>menu</span>
      </div>
      <div className={classes.nbody}>{(data.body || "").slice(0, 48) || "menu"}</div>
      <MultiOuts classes={classes} handles={handles} />
      <AddLegs nodeId={id} handles={handles} />
    </div>
  );
}

// frame tintado do bloco de repetição — desenhado ATRÁS dos passos (derivado em
// Builder a partir dos marcadores loop/loop_end; não-interativo, sem portas).
export function LoopFrameNode({ data }) {
  const classes = useStyles();
  const w = (data && data.w) || 308;
  const h = (data && data.h) || 120;
  const spineTop = 80; // abaixo do cabeçalho
  const spineH = Math.max(12, h - spineTop - 56); // até acima do rodapé
  return (
    <div className={classes.frame} style={{ width: w, height: h }}>
      <div className={classes.spine} style={{ top: spineTop, height: spineH, left: 14 }}>
        <span className={classes.spineDot} style={{ top: -3 }} />
        <span className={classes.spineDot} style={{ bottom: -3 }} />
      </div>
    </div>
  );
}

// Nó PROVISÓRIO "Criando automação…" (STU-160): aparece no canvas assim que o
// copiloto começa a montar um fluxo vazio — mesmo cartão dos nós reais, com
// animação (borda de gradiente girando + brilho passando) e a MESMA frase que o
// chat mostra (ação em andamento / aguardando você / pensando). Some
// quando a IA põe o 1º nó de verdade (que nasce no mesmo lugar). Só visual: não é
// salvo, não compila e a IA não o vê.
const PH_LIME = "#CEFF00";
const usePlaceholderStyles = makeStyles(theme => ({
  "@keyframes phSpinBorder": { to: { transform: "rotate(360deg)" } },
  "@keyframes phSweep": { "0%": { transform: "translateX(-120%)" }, "100%": { transform: "translateX(220%)" } },
  "@keyframes phBreath": { "0%, 100%": { transform: "scale(1)", filter: "drop-shadow(0 0 0 rgba(206,255,0,0))" }, "50%": { transform: "scale(1.12)", filter: "drop-shadow(0 0 6px rgba(140,180,0,.55))" } },
  "@keyframes phIn": { from: { opacity: 0, transform: "translateY(6px)" }, to: { opacity: 1, transform: "translateY(0)" } },
  // moldura: um gradiente cônico girando atrás do cartão, aparecendo só na borda
  ring: {
    position: "relative",
    width: 290,
    borderRadius: 12,
    padding: 1.5,
    overflow: "hidden",
    boxShadow: "0 6px 22px rgba(20,25,32,.10)",
    "&::before": {
      content: '""',
      position: "absolute",
      left: "50%",
      top: "50%",
      width: 600,
      height: 600,
      marginLeft: -300,
      marginTop: -300,
      background: `conic-gradient(from 0deg, transparent 0deg, ${PH_LIME} 60deg, #2C353D 120deg, transparent 180deg, transparent 360deg)`,
      animation: "$phSpinBorder 2.4s linear infinite",
    },
  },
  inner: {
    position: "relative",
    background: "#fff",
    borderRadius: 10.5,
    overflow: "hidden",
    // brilho que atravessa o cartão
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      bottom: 0,
      width: "45%",
      background: "linear-gradient(100deg, transparent, rgba(206,255,0,.16), transparent)",
      animation: "$phSweep 1.8s ease-in-out infinite",
      pointerEvents: "none",
    },
  },
  chip: {
    width: 30,
    height: 30,
    borderRadius: 8,
    background: "#2C353D",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& svg": { animation: "$phBreath 1.6s ease-in-out infinite" },
  },
  phrase: { display: "inline-block", animation: "$phIn .35s ease-out" },
  "@media (prefers-reduced-motion: reduce)": {
    ring: { "&::before": { animation: "none" } },
    inner: { "&::after": { animation: "none", display: "none" } },
    chip: { "& svg": { animation: "none" } },
  },
}));
const PlaceholderNode = ({ data }) => {
  const classes = useStyles();
  const c = usePlaceholderStyles();
  // a MESMA frase que o chat do copiloto mostra agora (vem do painel)
  const status = (data && data.status) || "Pensando";
  return (
    <div className={c.ring}>
      <div className={c.inner}>
        <Handle type="target" position={Position.Top} className={classes.port} style={{ opacity: 0 }} />
        <div className={classes.nhead}>
          <span className={c.chip}>
            {/* brilho de 4 pontas (o mesmo símbolo do copiloto) */}
            <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
              <path fill={PH_LIME} d="M12 1c.4 5.8 4.2 9.6 11 10-6.8.4-10.6 4.2-11 11-.4-6.8-4.2-10.6-11-11 6.8-.4 10.6-4.2 11-10z" />
            </svg>
          </span>
          <span className={classes.ntitle}>{(data && data.label) || "Criando automação"}</span>
        </div>
        <div className={classes.nbody}>
          <span key={status} className={c.phrase}>{status}…</span>
        </div>
      </div>
    </div>
  );
};

export const nodeTypes = {
  placeholderNode: PlaceholderNode,
  triggerNode: TriggerNode,
  startNode: TriggerNode, // legacy flows used "startNode"
  verbNode: VerbNode,
  branchNode: BranchNode,
  ivrNode: IvrNode,
  waMenuNode: WaMenuNode,
  loopFrame: LoopFrameNode,
};
